import { useEffect, useMemo, useRef, useState } from "react";
import { CascadePanel } from "./components/CascadePanel";
import { WaysPanel } from "./components/WaysPanel";
import { ChartsPanel } from "./components/ChartsPanel";
import { CloudGpuModal } from "./components/CloudGpuModal";
import { EngineBadge } from "./components/EngineBadge";
import { ErrorPanel } from "./components/ErrorPanel";
import { InsufficientSampleBadge } from "./components/InsufficientSampleBadge";
import { LastRunPanel } from "./components/LastRunPanel";
import { LegendPanel } from "./components/LegendPanel";
import { MetricsPanel } from "./components/MetricsPanel";
import { ProgressBar } from "./components/ProgressBar";
import { ReplayPanel } from "./components/ReplayPanel";
import { RtpConvergencePanel } from "./components/RtpConvergencePanel";
import { RuleTablePanel } from "./components/RuleTablePanel";
import { RunHistoryPanel } from "./components/RunHistoryPanel";
import { ValidationPanel } from "./components/ValidationPanel";
import { computeRtpSeries, type RtpSeriesPoint } from "./components/charts";
import { prependRun, type RunRecord } from "./runHistory";
import { classifyUploads } from "./uploadConfig";
import { readAggregatorOverrideFromUrl } from "./aggregatorOverride";
import { compileGame, type ValidationReport } from "./engine/compileGame";
import { createEngineAdapter } from "./engine/createEngineAdapter";
import { RemoteEngineAdapter } from "./engine/remote";
import { clearRemoteConfig } from "./engine/remoteConfig";
import { pickCascadeOutcome } from "./engine/cascade";
import {
  pickWaysOutcome,
  readWaysGame,
  type WaysOutcomePick,
} from "./engine/ways";
import { isMegawaysPlan, pickReplayOutcome } from "./engine/replay";
import type {
  EngineAdapter,
  MetricsResult,
  ReplayedOutcome,
  RunId,
} from "./engine/types";
import { useTheme } from "./themeContext";

// The default config is the canonical Tomb Explorer **DSL source** — compiled
// in-browser via `compile_game` on boot (spec 016 US4), the same path a user
// gets by dropping a raw `game.json`. The compile produces the IR + plan +
// config_hash together, so no pre-built `*.ir.json` / `*.plan.json` fixtures are
// fetched for the default anymore; this exercises the shipped no-server compile
// capability on first load rather than shipping a stale derived IR.
const DEFAULT_GAME_URL = "/tomb_explorer.game.json";
const N_SPINS_MIN = 1000;
const N_SPINS_MAX = 1_000_000;
const N_SPINS_DEFAULT = 10_000;

// MAD-1983 — GPU aggregator layout override from `?aggregator=packed|
// unpacked|auto`. Read once at module load (the URL doesn't change
// without a reload) and forwarded into every WebGPU metrics run so the
// same deployed bundle can be A/B-benchmarked across layouts/browsers.
// `undefined` (absent or unrecognised) leaves the production default.
const AGGREGATOR_OVERRIDE = readAggregatorOverrideFromUrl();

// RTP convergence chart: a dedicated small raw-path pass on the same seed
// as the main run (the large-N main run uses the on-device aggregator and
// yields no per-spin data). Deterministic in (config, seed), so these are
// bit-identical to the main run's first RTP_CURVE_SPINS spins.
const RTP_CURVE_SPINS = 10_000;
const RTP_ROLLING_WINDOW = 500;

// Spin replay (spec 018 US5): a second small sample pass over the CPU
// raw path, whose `state_path` detail carries the per-draw wild-mechanic
// payloads (locked masks, walks, expansions). 256 spins is enough to
// usually catch a feature trigger at typical scatter rates while staying
// trivially cheap on the CPU-WASM engine.
const REPLAY_SAMPLE_SPINS = 256;

// Large-N presets for the GPU-shader-aggregator path. 100M is the
// spec 006.6 T043 acceptance target: it completes in-browser without
// OOM because no per-spin wire bytes materialise (only the few-KB
// accumulator readback). 1B rides the same heap-flat path — only the
// accumulator readback is bounded, so memory stays flat regardless of
// N; the GPU dispatch loop shards it into device-sized chunks
// (compute_chunk_size), so the only cost over 100M is wall-clock time.
// 1B (1e9) fits the u32 n_spins boundary (max ~4.29e9). All require the
// plan + config-hash sidecars (i.e. the default Tomb Explorer IR).
const N_SPINS_PRESETS: ReadonlyArray<{ label: string; value: number }> = [
  { label: "1M", value: 1_000_000 },
  { label: "10M", value: 10_000_000 },
  { label: "100M", value: 100_000_000 },
  { label: "1B", value: 1_000_000_000 },
];
// Spin count at/above which a run uses the GPU-shader-aggregator path
// (matches the adapter's METRICS_THRESHOLD).
const METRICS_PRESET_MIN = 1_000_000;

type AdapterState =
  | { kind: "loading" }
  | { kind: "ready"; adapter: EngineAdapter }
  | { kind: "error"; error: unknown };

type RunState =
  | { kind: "idle" }
  | {
      kind: "running";
      runId: RunId;
      done: number;
      total: number | null;
      startedAt: number;
      // Latest streaming headline metrics for spins-so-far (WebGPU
      // aggregator path only); null until the first partial lands.
      partialMetrics: MetricsResult | null;
    }
  | { kind: "done"; metrics: MetricsResult; elapsedMs: number }
  | { kind: "error"; error: unknown };

export default function App() {
  const [adapterState, setAdapterState] = useState<AdapterState>({
    kind: "loading",
  });
  const [ir, setIr] = useState<Record<string, unknown> | null>(null);
  const [irLabel, setIrLabel] = useState<string>("loading default…");
  // Raw text of the plan + config-hash sidecars (passed verbatim to
  // run_webgpu_metrics). Non-null only for the default Tomb Explorer IR.
  const [planJson, setPlanJson] = useState<string | null>(null);
  const [configHash, setConfigHash] = useState<string | null>(null);
  // Spec 022 US3 — whether the active plan is a Megaways game (per-reel
  // `reel_height_distribution`). Drives the replay panel's per-spin
  // variable-height rendering + ways-count affordance. Parsed from the
  // plan sidecar; false for a fixed-grid (lines / fixed-ways) game or when
  // no plan is loaded.
  const isMegaways = useMemo<boolean>(() => {
    if (planJson == null) return false;
    try {
      return isMegawaysPlan(JSON.parse(planJson) as Record<string, unknown>);
    } catch {
      return false;
    }
  }, [planJson]);
  // Spec 016 US4 — the validation report from the last dropped raw `game.json`
  // (compiled in-browser via `compile_game`). Null when the current config came
  // from a pre-built IR upload or the default fetch. A report with non-empty
  // `errors` means the drop was rejected and the IR was left unchanged.
  const [validation, setValidation] = useState<ValidationReport | null>(null);
  const [seed, setSeed] = useState<string>(() => randomSeed());
  const [nSpins, setNSpins] = useState<number>(N_SPINS_DEFAULT);
  const [runState, setRunState] = useState<RunState>({ kind: "idle" });
  const runStateRef = useRef(runState);
  runStateRef.current = runState;
  // RTP-over-spins curve (first RTP_CURVE_SPINS), null until the first
  // sample pass completes for the current run.
  const [rtpPoints, setRtpPoints] = useState<RtpSeriesPoint[] | null>(null);
  // Spin-replay outcome (spec 018 US5): the most interesting spin of the
  // replay sample pass, null until that pass completes (or on failure).
  const [replay, setReplay] = useState<{
    outcome: ReplayedOutcome;
    index: number;
  } | null>(null);
  // Cascade-replay outcome (spec 019 US4): the deepest tumble of the
  // same replay sample pass, null when no sampled spin cascades (so the
  // panel stays hidden for non-cascading games) or on failure.
  const [cascade, setCascade] = useState<{
    outcome: ReplayedOutcome;
    index: number;
  } | null>(null);
  // Ways-win pick (spec 021 US3 / T027): the highest-paying ways win of
  // the same replay sample pass, null when the loaded game is not a ways
  // game or no sampled spin produced a ways win (so the panel stays
  // hidden for lines games and dead-spin samples).
  const [ways, setWays] = useState<WaysOutcomePick | null>(null);
  // True during the RTP-curve pre-pass, before the main run enters
  // `running`. Blocks a second Run click from scheduling an overlapping
  // pipeline in that window (the sample pass + main run share one GPU).
  const [isPreparingRun, setIsPreparingRun] = useState(false);
  // Session run history (newest first, capped) — in-memory by design.
  const [runHistory, setRunHistory] = useState<RunRecord[]>([]);
  const nextRunRecordId = useRef(1);
  // Spec 010 (T036/T038): Cloud GPU connect modal. Open via the
  // backend-select dropdown next to the EngineBadge.
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // Adapter init — runs once on mount.
  useEffect(() => {
    let cancelled = false;
    createEngineAdapter("auto")
      .then((adapter) => {
        if (!cancelled) setAdapterState({ kind: "ready", adapter });
      })
      .catch((error) => {
        if (!cancelled) setAdapterState({ kind: "error", error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Default config — runs once on mount. Fetch the canonical Tomb Explorer
  // DSL `game.json` and compile it in-browser via `compile_game` (spec 016 US4),
  // exactly as a user drop does (`onPickGameSource`). The compile yields the
  // IR + plan + config_hash together, so the large-N GPU-shader-aggregator path
  // is unlocked on first load without any pre-built fixture. A fetch/compile
  // failure surfaces in the error panel (same failure mode as a bad upload).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(DEFAULT_GAME_URL);
        if (!resp.ok)
          throw new Error(`fetch ${DEFAULT_GAME_URL} -> ${resp.status}`);
        const text = await resp.text();
        if (cancelled) return;
        // `onPickGameSource` appends "(compiled)", so the default boot reads
        // "/tomb_explorer.game.json (compiled)" — signalling the US4 in-browser
        // compile ran rather than a pre-built IR being fetched.
        await onPickGameSource({ text, label: DEFAULT_GAME_URL });
      } catch (err) {
        if (!cancelled) {
          setIr(null);
          setIrLabel(
            `error: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Mount-once: onPickGameSource is a stable component method; the default
    // config must not recompile on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onPickFiles(files: readonly File[]) {
    // Read all dropped files to text, then classify by content (IR /
    // plan / config-hash). Dropping the IR alone still works — it just
    // routes large-N runs to the raw path; adding the sibling
    // plan.json unlocks the GPU-shader-aggregator path + attribution
    // panels (MAD-1538). The plan embeds its own config_hash, so
    // IR + plan is sufficient (no separate hash file needed).
    try {
      const read = await Promise.all(
        files.map(async (f) => ({ name: f.name, text: await f.text() })),
      );
      const result = classifyUploads(read);
      if (result.errors.length > 0) {
        setRunState({
          kind: "error",
          error: new Error(`Upload rejected: ${result.errors.join("; ")}`),
        });
        return;
      }

      // Spec 016 US4 — a raw DSL `game.json` drop: compile it in-browser via
      // `compile_game` (parse → validate → lower), render the validation
      // report, and on a clean compile feed the IR + plan + config_hash to the
      // runtime. No server, no pre-built IR file required.
      if (result.gameSource != null) {
        await onPickGameSource(result.gameSource);
        return;
      }

      if (result.ir == null) {
        setRunState({
          kind: "error",
          error: new Error(
            "No config found. Drop a raw game.json, or a *.ir.json (optionally with its *.plan.json to unlock large-N runs).",
          ),
        });
        return;
      }
      // A pre-built IR upload: clear any stale validation report from a prior
      // game.json drop.
      setValidation(null);
      setIr(result.ir.json);
      setIrLabel(result.ir.label);
      // null when no plan was supplied → large-N runs fall back to the
      // raw outcome path (current single-IR-upload behaviour preserved).
      setPlanJson(result.planJson);
      setConfigHash(result.configHash);
    } catch (err) {
      setRunState({ kind: "error", error: err });
    }
  }

  /**
   * Spec 016 US4 — compile a dropped raw `game.json` and wire the result into
   * the runtime. On a clean compile the IR + plan + config_hash replace the
   * current config (so a Run uses the dropped game); on validation errors the
   * report is shown and the existing config is left untouched. Infra failures
   * (WASM not loading) surface in the error panel.
   */
  async function onPickGameSource(gameSource: { text: string; label: string }) {
    let output;
    try {
      output = await compileGame(gameSource.text);
    } catch (err) {
      // A genuine infrastructure failure (module load / contract breach), not a
      // rejected config — surface it distinctly from validation feedback.
      setRunState({ kind: "error", error: err });
      return;
    }
    setValidation(output.validation);
    if (output.ir == null) {
      // Rejected config: `ir`/`plan`/`config_hash` are gated off together. Keep
      // the current config; the ValidationPanel shows the codes/locations/fixes.
      return;
    }
    setIr(output.ir);
    setIrLabel(`${gameSource.label} (compiled)`);
    // `plan` + `config_hash` arrive together with the IR (gated identically),
    // unlocking the large-N GPU-aggregator path exactly like an uploaded
    // *.plan.json sidecar. `plan` crosses to `run_webgpu_metrics` as text.
    setPlanJson(output.plan != null ? JSON.stringify(output.plan) : null);
    setConfigHash(output.config_hash);
  }

  function onRun() {
    if (adapterState.kind !== "ready" || ir == null) return;
    // Re-entrancy guard: the RTP pre-pass runs before the main run enters
    // `running`, so without this a second Run click would schedule an
    // overlapping pipeline (both dispatch on the one GPU).
    if (isPreparingRun || runStateRef.current.kind === "running") return;
    const { adapter } = adapterState;
    const config = ir;
    // Snapshot — the seed input stays enabled during the pre-passes, so a
    // mid-flight edit must not split the RTP curve / replay sample / main
    // run across different seeds.
    const capturedSeed = seed;

    // RTP convergence curve: a small, deterministic raw-path pass on the
    // same seed, run BEFORE the main sim so the two don't dispatch on the
    // GPU concurrently. Best-effort — a failure clears the curve but never
    // blocks the main run (which always starts in `.finally`).
    setIsPreparingRun(true);
    setRtpPoints(null);
    setReplay(null);
    setCascade(null);
    setWays(null);
    adapter
      .sampleWinnings({ config, nSpins: RTP_CURVE_SPINS, seed: capturedSeed })
      .then((winnings) =>
        setRtpPoints(
          computeRtpSeries(winnings, { window: RTP_ROLLING_WINDOW }),
        ),
      )
      .catch(() => setRtpPoints(null))
      // Spin-replay sample (spec 018 US5): a small CPU raw pass whose
      // state_path detail drives the ReplayPanel. Best-effort like the
      // RTP curve — a failure clears the panel, never blocks the run.
      .then(() =>
        adapter.sampleOutcomes({
          config,
          nSpins: REPLAY_SAMPLE_SPINS,
          seed: capturedSeed,
        }),
      )
      // Same sample pass drives both the per-draw replay (spec 018 US5)
      // and the cascade tumble (spec 019 US4) — the cascade pick is the
      // deepest tumble, null when nothing cascaded.
      .then((outcomes) => {
        setReplay(pickReplayOutcome(outcomes));
        setCascade(pickCascadeOutcome(outcomes));
        // Ways-win affordance (spec 021 US3): reconstruct ways wins from
        // the sampled grids when the loaded game is a ways game. `null`
        // for lines games (readWaysGame returns null) or a dead sample.
        const waysInfo = readWaysGame(config);
        setWays(waysInfo == null ? null : pickWaysOutcome(outcomes, waysInfo));
      })
      .catch(() => {
        setReplay(null);
        setCascade(null);
        setWays(null);
      })
      .finally(() => {
        setIsPreparingRun(false);
        startMainRun();
      });

    function startMainRun() {
      const startedAt = performance.now();
      const runSeed = capturedSeed;
      const [runId, promise] = adapter.run(
        {
          config,
          nSpins,
          seed: capturedSeed,
          ...(planJson != null ? { planJson } : {}),
          ...(configHash != null ? { configHash } : {}),
          ...(AGGREGATOR_OVERRIDE != null
            ? { aggregatorLayout: AGGREGATOR_OVERRIDE }
            : {}),
        },
        (done, total, partial) => {
          // Functional updater (NOT a ref snapshot): streaming interleaves
          // count ticks (partial == null) and partial ticks (partial != null)
          // with no React render between them, so `runStateRef.current` goes
          // stale within a burst. A ref-based merge let the count tick that
          // fires right after each partial clobber `partialMetrics` back to
          // null (`null ?? staleNull`), so the live charts never saw a value.
          // Merging onto `prev` keeps the last partial sticky. (MAD-1436)
          setRunState((prev) => {
            if (prev.kind !== "running" || prev.runId !== runId) return prev;
            return {
              ...prev,
              done,
              total,
              partialMetrics: partial ?? prev.partialMetrics,
            };
          });
        },
      );
      setRunState({
        kind: "running",
        runId,
        done: 0,
        total: null,
        startedAt,
        partialMetrics: null,
      });
      promise
        .then((metrics) => {
          if (
            runStateRef.current.kind === "running" &&
            runStateRef.current.runId === runId
          ) {
            const elapsedMs = performance.now() - startedAt;
            setRunState({ kind: "done", metrics, elapsedMs });
            setRunHistory((h) =>
              prependRun(h, {
                id: nextRunRecordId.current++,
                completedAt: Date.now(),
                seed: runSeed,
                nSpins: metrics.n_spins,
                configHash: metrics.config_hash,
                engineLabel: adapter.mode === "webgpu" ? "webgpu" : "cpu-wasm",
                elapsedMs,
                rtpPct: metrics.rtp.point * 100,
                ciHalfPct: metrics.rtp.ci_half_width * 100,
                hitPct: metrics.hit_frequency * 100,
                stdev: metrics.volatility.stdev,
                maxWin: metrics.max_win_observed,
              }),
            );
          }
        })
        .catch((error) => {
          if (
            runStateRef.current.kind === "running" &&
            runStateRef.current.runId === runId
          ) {
            setRunState({ kind: "error", error });
          }
        });
    }
  }

  function onCancel() {
    if (adapterState.kind === "ready" && runState.kind === "running") {
      adapterState.adapter.cancel(runState.runId);
    }
  }

  function onReseed() {
    setSeed(randomSeed());
  }

  /**
   * Spec 010 T038 — backend-select dropdown adjacent to the
   * `EngineBadge`. Two options today: "Local (auto)" rebuilds via
   * `createEngineAdapter("auto")`; "Cloud GPU" opens the
   * `CloudGpuModal`. Disabled while a run is in flight (swapping
   * adapter mid-run would orphan the cancel + progress callbacks).
   */
  function renderBackendSelect() {
    const isRemote =
      adapterState.kind === "ready" &&
      adapterState.adapter instanceof RemoteEngineAdapter;
    const value = isRemote ? "cloud" : "local";
    // Disabled while a run is in flight (mid-run adapter swap would
    // orphan cancel + progress handles). Allowed in `loading` /
    // `error` states so users on unsupported local hardware can
    // still reach the Cloud GPU modal.
    const disabled = runState.kind === "running" || isPreparingRun;

    return (
      <select
        aria-label="Simulation backend"
        data-testid="backend-select"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          if (next === "cloud") {
            setCloudModalOpen(true);
            return;
          }
          if (next === "local" && isRemote) {
            clearRemoteConfig();
            setAdapterState({ kind: "loading" });
            createEngineAdapter("auto")
              .then((adapter) => setAdapterState({ kind: "ready", adapter }))
              .catch((error) => setAdapterState({ kind: "error", error }));
          }
        }}
        className="border border-paper-border bg-paper-bg px-2 py-1 text-xs text-paper-text hover:bg-paper-panel disabled:cursor-not-allowed disabled:opacity-50 dark:border-term-border dark:bg-term-bg dark:text-term-text dark:hover:bg-term-panel"
      >
        <option value="local">Local (auto)</option>
        <option value="cloud">Cloud GPU…</option>
      </select>
    );
  }

  // True when the results column has nothing to show yet — render an
  // empty-state hint instead of a blank void.
  const resultsEmpty =
    runHistory.length === 0 &&
    (runState.kind === "idle" ||
      (runState.kind === "running" && runState.partialMetrics == null)) &&
    (rtpPoints == null || rtpPoints.length === 0);

  // Whichever metrics are currently live: final result when done,
  // streaming partials while running, otherwise none.
  const activeMetrics =
    runState.kind === "done"
      ? runState.metrics
      : runState.kind === "running"
        ? runState.partialMetrics
        : null;

  return (
    <main className="min-h-screen p-6 font-mono text-sm">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex items-center justify-between gap-3 border-b border-paper-border pb-3 dark:border-term-border">
          <div className="flex items-baseline gap-3">
            <h1 className="text-lg font-bold tracking-wide text-paper-bright dark:text-term-bright">
              SLOT-MATH
              <span className="text-emerald-700 dark:text-emerald-500">
                ://
              </span>
              SIM
            </h1>
            <span className="text-[10px] text-paper-hint dark:text-term-hint">
              simulation launcher
            </span>
          </div>
          <div className="flex items-center gap-2">
            {adapterState.kind === "ready" && (
              <EngineBadge adapter={adapterState.adapter} />
            )}
            {/* Backend select stays visible even in error/loading
                states so users on unsupported local hardware can
                still reach the Cloud GPU modal (CR catch). */}
            {renderBackendSelect()}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              data-testid="theme-toggle"
              className="border border-paper-border bg-paper-bg p-1.5 text-paper-text hover:bg-paper-panel hover:text-paper-bright dark:border-term-border dark:bg-term-bg dark:text-term-text dark:hover:bg-term-panel dark:hover:text-term-bright"
            >
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </header>

        <CloudGpuModal
          open={cloudModalOpen}
          onClose={() => setCloudModalOpen(false)}
          onSuccess={(adapter) => {
            setAdapterState({ kind: "ready", adapter });
            setCloudModalOpen(false);
          }}
        />

        {/* Two-zone layout: controls rail (left, sticky) + results
            (right, wide). Collapses to a single column below lg. */}
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]">
          <div className="space-y-4 lg:sticky lg:top-6">
            {adapterState.kind === "loading" && (
              <p className="border border-paper-border bg-paper-panel p-3 text-paper-text dark:border-term-border dark:bg-term-panel dark:text-term-text">
                Initialising engine…
              </p>
            )}

            {adapterState.kind === "error" && (
              <ErrorPanel err={adapterState.error} />
            )}

            {adapterState.kind === "ready" && (
              <section className="space-y-3">
                <RailPanel label="Config">
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer border border-paper-border bg-paper-panel px-3 py-1.5 text-sm text-paper-bright hover:bg-paper-border/40 dark:border-term-border dark:bg-term-panel dark:text-term-bright dark:hover:bg-term-border/40">
                      Upload…
                      <input
                        type="file"
                        accept=".json,application/json,.config_hash"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) {
                            void onPickFiles(Array.from(files));
                          }
                        }}
                      />
                    </label>
                    <span className="truncate text-xs text-paper-text dark:text-term-text">
                      {irLabel}
                    </span>
                  </div>
                  <div className="mt-2 text-[10px] text-paper-hint dark:text-term-hint">
                    {configHash != null
                      ? `hash ${configHash.slice(0, 4)}…${configHash.slice(-4)} · plan ${planJson != null ? "✓" : "—"}`
                      : "drop a game.json to compile · or an IR (+ .plan.json for large-N)"}
                  </div>
                </RailPanel>

                <RailPanel label="Seed">
                  <div className="flex items-center gap-2">
                    <input
                      id="seed-input"
                      aria-label="Seed (16-char hex)"
                      type="text"
                      value={seed}
                      onChange={(e) =>
                        setSeed(e.target.value.trim().toLowerCase())
                      }
                      className="w-full border border-paper-border bg-paper-panel px-2 py-1.5 font-mono text-sm text-emerald-700 focus:border-emerald-700 focus:outline-none dark:border-term-border dark:bg-term-panel dark:text-emerald-500 dark:focus:border-emerald-500"
                      maxLength={16}
                      pattern="[0-9a-f]{16}"
                    />
                    <button
                      type="button"
                      onClick={onReseed}
                      title="Reroll seed"
                      className="border border-paper-border px-2 py-1.5 text-xs text-paper-text hover:bg-paper-panel dark:border-term-border dark:text-term-text dark:hover:bg-term-panel"
                    >
                      ⟳
                    </button>
                  </div>
                </RailPanel>

                <RailPanel label="Spins">
                  <div className="flex items-baseline justify-between">
                    <label
                      htmlFor="n-spins-input"
                      className="text-[10px] uppercase tracking-widest text-paper-label dark:text-term-label"
                    >
                      count
                    </label>
                    <span className="text-sm tabular-nums text-paper-bright dark:text-term-bright">
                      {nSpins.toLocaleString()}
                    </span>
                  </div>
                  <input
                    id="n-spins-input"
                    type="range"
                    min={N_SPINS_MIN}
                    max={N_SPINS_MAX}
                    step={1000}
                    value={Math.min(nSpins, N_SPINS_MAX)}
                    onChange={(e) => setNSpins(Number(e.target.value))}
                    className="mt-2 w-full accent-emerald-700 dark:accent-emerald-500"
                  />
                  <div className="mt-2 flex items-center gap-1.5">
                    {N_SPINS_PRESETS.map((preset) => {
                      const enabled =
                        adapterState.adapter.mode === "webgpu" &&
                        planJson != null;
                      return (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => setNSpins(preset.value)}
                          disabled={!enabled}
                          title={
                            enabled
                              ? `GPU-shader-aggregator path — ${preset.label} spins`
                              : adapterState.adapter.mode === "webgpu"
                                ? "Large-N presets need the plan sidecar — upload the .plan.json alongside the IR"
                                : "Large-N presets need the WebGPU backend"
                          }
                          className={`border px-2 py-0.5 text-[11px] ${
                            nSpins === preset.value
                              ? "border-emerald-700 bg-emerald-700/10 text-emerald-700 dark:border-emerald-500 dark:bg-emerald-500/10 dark:text-emerald-400"
                              : "border-paper-border text-paper-text hover:bg-paper-panel dark:border-term-border dark:text-term-text dark:hover:bg-term-panel"
                          } disabled:cursor-not-allowed disabled:opacity-40`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                  {nSpins >= METRICS_PRESET_MIN && (
                    <p className="mt-2 text-[10px] text-paper-hint dark:text-term-hint">
                      {adapterState.adapter.mode === "webgpu" &&
                      planJson != null
                        ? "gpu-shader-aggregator · heap-flat"
                        : "raw outcome path — may exhaust JS heap"}
                    </p>
                  )}
                </RailPanel>

                {runState.kind === "running" ? (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="w-full bg-rose-700 px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-rose-600 dark:bg-rose-600 dark:hover:bg-rose-500"
                  >
                    ■ Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onRun}
                    disabled={
                      isPreparingRun ||
                      ir == null ||
                      !/^[0-9a-f]{16}$/.test(seed)
                    }
                    className="w-full bg-emerald-700 px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-paper-border disabled:text-paper-label dark:bg-emerald-600 dark:text-term-bg dark:hover:bg-emerald-500 dark:disabled:bg-term-border dark:disabled:text-term-label"
                  >
                    {isPreparingRun ? "Preparing run…" : "▶ Run simulation"}
                  </button>
                )}

                {runState.kind === "running" && (
                  <ProgressBar
                    done={runState.done}
                    total={runState.total}
                    engine={
                      adapterState.kind === "ready"
                        ? adapterState.adapter.mode
                        : undefined
                    }
                    spinsPerSec={
                      runState.done > 0
                        ? runState.done /
                          ((performance.now() - runState.startedAt) / 1000)
                        : null
                    }
                  />
                )}

                {runState.kind === "done" && (
                  <LastRunPanel
                    elapsedMs={runState.elapsedMs}
                    nSpins={runState.metrics.n_spins}
                    engineLabel={
                      adapterState.adapter.mode === "webgpu"
                        ? "webgpu"
                        : "cpu-wasm"
                    }
                  />
                )}
              </section>
            )}

            <LegendPanel />
          </div>

          <div className="min-w-0 space-y-6">
            {validation != null && (
              <ValidationPanel
                report={validation}
                label={irLabel}
                onDismiss={() => setValidation(null)}
              />
            )}

            {runState.kind === "running" && runState.partialMetrics != null && (
              <>
                <MetricsPanel metrics={runState.partialMetrics} streaming />
                <ChartsPanel metrics={runState.partialMetrics} streaming />
              </>
            )}

            {runState.kind === "error" && (
              <ErrorPanel
                err={runState.error}
                onDismiss={() => setRunState({ kind: "idle" })}
              />
            )}

            {runState.kind === "done" && (
              <>
                <MetricsPanel metrics={runState.metrics} />
                <ChartsPanel metrics={runState.metrics} />
                <InsufficientSampleBadge
                  flags={runState.metrics.insufficient_sample_flags}
                />
              </>
            )}

            {rtpPoints != null && rtpPoints.length > 0 && (
              <RtpConvergencePanel
                points={rtpPoints}
                window={RTP_ROLLING_WINDOW}
              />
            )}

            {/* Spin-by-spin replay with wild-mechanic indicators
                (spec 018 US5): sticky locks, walking motion,
                expanding reel-fill. */}
            {replay != null && (
              <ReplayPanel
                outcome={replay.outcome}
                spinIndex={replay.index}
                megaways={isMegaways}
              />
            )}

            {/* Round-by-round cascade (tumble) sequence (spec 019 US4):
                winners vacate, refill drops in, accumulating per-round
                multiplier. Shown only when a sampled spin cascaded. */}
            {cascade != null && (
              <CascadePanel
                outcome={cascade.outcome}
                spinIndex={cascade.index}
              />
            )}

            {/* Ways-win display (spec 021 US3): a sampled ways win with its
                run length, ways count, and leading symbol. Shown only for
                ways games with a winning sampled spin — no paylines to draw. */}
            {ways != null && <WaysPanel pick={ways} />}

            {/* Pay rules below the convergence chart — fed by whichever
                metrics are live (streaming partials or the final result). */}
            {activeMetrics != null && (
              <RuleTablePanel
                rtpPerRule={activeMetrics.rtp_per_rule}
                hitsPerRule={activeMetrics.hits_per_rule}
                nSpins={activeMetrics.n_spins}
              />
            )}

            <RunHistoryPanel history={runHistory} />

            {resultsEmpty && (
              <div className="flex min-h-[16rem] items-center justify-center border border-dashed border-paper-border text-sm text-paper-hint dark:border-term-border dark:text-term-hint">
                {runState.kind === "running"
                  ? "Simulation running — metrics land here…"
                  : "Run a simulation to see metrics and charts."}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

/** Bordered rail panel with an uppercase terminal label. */
function RailPanel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-paper-border p-3 dark:border-term-border">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-paper-bright dark:text-term-bright">
        {label}
      </div>
      {children}
    </div>
  );
}

function randomSeed(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Sun icon (shown in dark mode — click for light). Lucide outline. */
function SunIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

/** Moon icon (shown in light mode — click for dark). Lucide outline. */
function MoonIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}
